import { type ReactElement, type ReactNode, useId } from "react";
import {
  Box,
  FormControl,
  type FormControlProps,
  FormHelperText,
  Typography,
} from "@mui/material";

import { TimePicker, TimePickerProps } from "@mui/x-date-pickers";
import { format, parse } from "date-fns";
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";

type TextInputProps<T extends FieldValues> = {
  fieldName: Path<T>;
  label?: ReactNode;
  size?: FormControlProps["size"];
  includeSeconds?: boolean;
  hideLabel?: boolean;
  hideError?: boolean;
  fullWidth?: boolean;
  hide?: boolean;
  control: Control<T>;
} & TimePickerProps;

export const TimeInput = <T extends FieldValues>({
  fieldName,
  label,
  hideLabel = false,
  hideError = false,
  includeSeconds = true,
  size,
  fullWidth,
  control,
  hide,
  ...inputProps
}: TextInputProps<T>): ReactElement | null => {
  const id = useId();

  const {
    field: { ...field },
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
  });

  if (hide) {
    return null;
  }

  const formatString = includeSeconds ? "HH:mm:ss" : "HH:mm";

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}
    >
      {!hideLabel && <Typography id={id}>{label}</Typography>}
      <FormControl fullWidth={fullWidth} error={!!error} size={size}>
        <TimePicker
          {...inputProps}
          {...field}
          slotProps={{
            textField: {
              "aria-labelledby": id,
            },
          }}
          value={
            field.value ? parse(field.value, formatString, new Date()) : null
          }
          onChange={(value) =>
            field.onChange(value ? format(value, formatString) : value)
          }
        />
        {!hideError && error?.message && (
          <FormHelperText>{error.message}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};
